import React, { useEffect, useMemo, useState, useRef } from "react";
import { Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/shadcn/popover";
import { cn } from "../../../lib/utils";

export type OptionType = {
  value: string;
  label: string;
  icon?: string;
};

export interface SelectInlineProps {
  options: OptionType[];
  value?: string | null;
  onChange: (id: string) => void;
  placeholder?: string;
  autoOpen?: boolean; // when true, open the menu immediately
  menuPlacement?: "auto" | "bottom" | "top";
  customTrigger?: React.ReactNode; // optional custom trigger element
  width?: string; // Width for trigger button
  maxWidth?: string; // Deprecated: use width instead
  popoverMinWidth?: string; // Min width for popover content (default: 282px)
}

export default function SelectInline({
  options = [],
  value,
  onChange,
  placeholder,
  autoOpen = false,
  menuPlacement: _menuPlacement = "auto",
  customTrigger,
  width,
  maxWidth,
  popoverMinWidth = "282px",
}: SelectInlineProps) {
  // Computed values
  const componentWidth = width || maxWidth;

  // State
  const [open, setOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [triggerHeight, setTriggerHeight] = useState<number>(customTrigger ? 20 : 28);
  const [triggerWidth, setTriggerWidth] = useState<number | null>(null);

  // Refs
  const triggerRef = useRef<HTMLButtonElement>(null);
  const customTriggerRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);

  // Ensure menu opens when entering edit mode
  useEffect(() => {
    setOpen(!!autoOpen);
  }, [autoOpen]);

  const active = useMemo(() => {
    if (!options || typeof value === "undefined" || value === null) return null;
    return (options as OptionType[]).find((o) => String(o.value) === String(value)) ?? null;
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((opt) => {
      const label = String(opt.label ?? "").toLowerCase();
      const val = String(opt.value ?? "").toLowerCase();
      return label.includes(q) || val.includes(q);
    });
  }, [options, search]);

  // Reset search when menu closes
  useEffect(() => {
    if (!open) {
      setSearch("");
    }
  }, [open]);

  // Calculate trigger dimensions when popover opens
  useEffect(() => {
    if (!open) return;

    const rafId = requestAnimationFrame(() => {
      if (customTrigger) {
        const containerWidth = containerRef.current?.offsetWidth;
        if (containerWidth) {
          setTriggerWidth(containerWidth);
        }
        if (customTriggerRef.current) {
          setTriggerHeight(customTriggerRef.current.offsetHeight);
        }
      } else if (triggerRef.current) {
        setTriggerHeight(triggerRef.current.offsetHeight);
        setTriggerWidth(triggerRef.current.offsetWidth);
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [open, customTrigger]);

  // Helper: Get popover content width style
  const getPopoverWidthStyle = (): React.CSSProperties => {
    if (customTrigger && triggerWidth) {
      return { width: `${triggerWidth}px`, minWidth: popoverMinWidth };
    }
    if (customTrigger) {
      return { width: componentWidth || "100%", minWidth: popoverMinWidth };
    }
    if (componentWidth) {
      const width =
        componentWidth === "100%" ? "var(--radix-popover-trigger-width)" : componentWidth;
      return { width, minWidth: popoverMinWidth };
    }
    return { width: "var(--radix-popover-trigger-width)", minWidth: popoverMinWidth };
  };

  // Helper: Forward ref for custom trigger element
  const forwardCustomTriggerRef = (node: HTMLElement | null) => {
    customTriggerRef.current = node;
    const originalRef = (customTrigger as any).ref;
    if (originalRef) {
      if (typeof originalRef === "function") {
        originalRef(node);
      } else {
        originalRef.current = node;
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className="w-full"
      style={componentWidth ? { width: componentWidth, maxWidth: componentWidth } : {}}
    >
      <Popover open={open} modal={false} onOpenChange={setOpen}>
        {customTrigger ? (
          <PopoverTrigger asChild>
            {React.isValidElement(customTrigger) ? (
              React.cloneElement(customTrigger as any, {
                ref: forwardCustomTriggerRef,
                onClick: (e: React.MouseEvent) => {
                  setOpen(true);
                  (customTrigger as any).props?.onClick?.(e);
                },
                className: cn("cursor-pointer", (customTrigger as any).props?.className),
              })
            ) : (
              <div
                ref={(node) => {
                  customTriggerRef.current = node;
                }}
                onClick={() => setOpen(true)}
                className="cursor-pointer"
              >
                {customTrigger}
              </div>
            )}
          </PopoverTrigger>
        ) : (
          <PopoverTrigger asChild>
            <button
              ref={triggerRef}
              type="button"
              className={cn(
                "flex items-center justify-between h-7 px-2 text-xs leading-4 min-w-[120px] bg-white",
                "focus:outline-none focus:ring-0 focus-visible:ring-0 border-0",
                !active && "text-gray-500"
              )}
              style={componentWidth ? { width: componentWidth, minWidth: componentWidth } : {}}
              onClick={() => setOpen(true)}
            >
              <div className="flex items-center gap-2">
                {active ? (
                  <>
                    {active.icon && <img src={active.icon} alt="" className="w-4 h-4 rounded" />}
                    <span className="text-gray-800">{active.label}</span>
                  </>
                ) : (
                  <span className="text-gray-500">{placeholder || "Select..."}</span>
                )}
              </div>
            </button>
          </PopoverTrigger>
        )}
        <PopoverContent
          ref={popoverContentRef}
          className={cn("p-0 bg-white border border-gray-200 shadow-lg transition-none")}
          style={{
            ...getPopoverWidthStyle(),
            zIndex: 100,
            marginTop: `-${triggerHeight}px`,
          }}
          onOpenAutoFocus={() => {
            if (customTrigger && !triggerWidth && containerRef.current) {
              const containerWidth = containerRef.current.offsetWidth;
              if (containerWidth) {
                setTriggerWidth(containerWidth);
              }
            }
          }}
          align="start"
          side="bottom"
          sideOffset={0}
          onClick={(e) => {
            // Prevent event from bubbling up and closing the popover
            e.stopPropagation();
          }}
          onPointerDownOutside={(e) => {
            const target = e.target as HTMLElement;
            // Prevent closing when clicking inside the popover content
            if (popoverContentRef.current && popoverContentRef.current.contains(target)) {
              e.preventDefault();
            }
          }}
          onFocusOutside={(e) => {
            const target = e.target as HTMLElement;
            // Prevent closing when focusing inside the popover content
            if (popoverContentRef.current && popoverContentRef.current.contains(target)) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement;
            // Prevent closing when interacting inside the popover content
            if (popoverContentRef.current && popoverContentRef.current.contains(target)) {
              e.preventDefault();
            }
          }}
        >
          <div
            onClick={(e) => {
              // Prevent event from bubbling up and closing the popover
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              // Prevent event from bubbling up and closing the popover
              e.stopPropagation();
            }}
          >
            <div className="px-2 py-1.5 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={placeholder || "Search..."}
                  className="w-full pl-8 pr-2 h-7 text-sm bg-transparent border-0 focus:outline-none focus:ring-0 placeholder:text-gray-400"
                />
              </div>
            </div>

            <div className="py-1 max-h-64 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-500">No records</div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = String(option.value) === String(active?.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onPointerDownCapture={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onMouseDownCapture={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onChange(String(option.value));
                        setOpen(false);
                      }}
                      className={cn(
                        "w-full flex items-center gap-2 px-3 py-2 text-[12px] leading-4 text-left hover:bg-gray-100 transition-colors",
                        isSelected && "bg-blue-50"
                      )}
                    >
                      {option.icon && <img src={option.icon} alt="" className="w-4 h-4 rounded" />}
                      <span className="truncate">{option.label}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
